import React, { useContext, useMemo } from 'react';

import { TransferAccountsState, useTransferFromAndToAccount } from '../../../../hooks/useAccountState';
import { useForm, Form, CreateForm } from '../../../../hooks/useForm';
import { DownpaymentAction } from './actions';
import { DownpaymentState } from './state';
import useCreateDownpayment, { DownpaymentMutation } from './useCreateDownpayment';
import { DownpaymentError } from './validate';

export type FormWithMutate<TState, TAction, TMutation, TError> = {
    mutation: TMutation;
} & Form<TState, TAction, TError>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormContextWithMutate = React.createContext<FormWithMutate<any, any, any, any> | undefined>(undefined);

type DownpaymentContext = FormWithMutate<DownpaymentState, DownpaymentAction, DownpaymentMutation, DownpaymentError>;

export default () => {
    const context = useContext(FormContextWithMutate)! as DownpaymentContext;
    if (context === undefined) {
        throw new Error('useFormContext must be used within FormContextWithMutate');
    }

    return context;
};

export const DownpaymentFormContext = ({
    validateHook,
    reducer,
    initialState,
    children,
}: CreateForm<DownpaymentState, DownpaymentAction, DownpaymentError>) => {
    const form = useForm<DownpaymentState, DownpaymentAction, DownpaymentError>(validateHook, reducer, initialState);
    const downpaymentToFromContext = useTransferFromAndToAccount(form.state.fromAccount, form.state.toAccount);
    const createDownpayment = useCreateDownpayment(
        downpaymentToFromContext.targetFromAccount,
        downpaymentToFromContext.targetToAccount,
    );
    const contextValue = useMemo(() => ({ ...form, mutation: createDownpayment }), [form, createDownpayment]);
    return (
        <FormContextWithMutate.Provider value={contextValue}>
            <TransferAccountsState.Provider value={downpaymentToFromContext}>{children}</TransferAccountsState.Provider>
        </FormContextWithMutate.Provider>
    );
};
